import React from 'react';

/**
 * Data honesty, stated inline beside the cohort it affects.
 *
 * Task.completed_date is a recent column; older finished work -- especially
 * anything wrapped up before the column existed -- can be missing it, so
 * `completion_date_coverage` below 1 means the Completed count itself is an
 * undercount, not just the intermediate stages.
 *
 * Indeterminacy is a property of a GROUP, not of the cohort. `known` on a
 * group answers a narrow question per record: could this record's stage be
 * determined at all -- because it's complete, because it has a logged status
 * change, or because it was created after its model's audit log began? When
 * a record in that group fails all three, its position is genuinely
 * unknowable, so THAT GROUP's intermediate (pre-Completed) stages report
 * null rather than a guessed or zeroed count. The groups beside it are
 * untouched: one unknowable Other task says nothing about the corporate
 * numbers, and caveating those too would smear a gap over figures that are
 * not in doubt.
 *
 * So the notice names the affected groups. `stage_counts_available` on the
 * cohort is the OR of its groups -- "are any stage counts available here at
 * all" -- and is only consulted as a last resort, for a payload that
 * reports the gap without saying where it is.
 */
export default function CoverageNotice({ cohort }) {
  const coverage = cohort.completion_date_coverage;
  const incompleteCoverage = coverage !== null && coverage < 1;

  const unknownLabels = (cohort.groups || [])
    .filter((g) => g.known === false)
    .map((g) => g.label || g.key);
  // A cohort that reports its counts unavailable while no group owns up to
  // it -- a payload without per-group `known`, or one with no groups at
  // all. Saying it cohort-wide is vaguer than the truth but still true.
  const cohortWideUnavailable =
    unknownLabels.length === 0 && cohort.stage_counts_available === false;
  const stagesUnavailable = unknownLabels.length > 0 || cohortWideUnavailable;

  if (!incompleteCoverage && !stagesUnavailable) return null;

  return (
    <div
      role="alert"
      style={{
        background: '#FFFBEB',
        border: '1px solid #FDE68A',
        color: '#92400E',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 13,
        lineHeight: 1.5,
        margin: '6px 0',
      }}
    >
      {incompleteCoverage ? (
        <div>
          FY{cohort.fiscal_year}: {Math.round(coverage * 100)}% of completed
          tasks have a completion date — this cohort's Completed count is
          under-counted.
        </div>
      ) : null}
      {stagesUnavailable ? (
        <div>
          FY{cohort.fiscal_year}: stage counts unavailable for{' '}
          {cohortWideUnavailable ? 'this cohort' : unknownLabels.join(', ')} —
          at least one task's position there could not be determined, so the
          stages before Completed show as unavailable, not zero.
          {cohortWideUnavailable ? null : ' Every other group in this year is unaffected.'}
        </div>
      ) : null}
    </div>
  );
}
